import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Sparkles, 
  Home, 
  Sun, 
  Car,
  Phone, 
  MapPin, 
  Menu, 
  X, 
  CheckCircle2, 
  ArrowRight,
  Shield,
  Leaf,
  Clock,
  Star,
  Cookie
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MobileBottomNav from "@/components/MobileBottomNav";
import TestimonialsSection from "@/components/TestimonialsSection";
import PreFooterBanner from "@/components/PreFooterBanner";

const ServicePage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ServicePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cookie Consent Banner Component
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 bg-white border-t border-slate-200 shadow-2xl animate-fade-in-up"
      data-testid="cookie-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="hidden md:flex w-12 h-12 rounded-xl bg-sky-100 items-center justify-center flex-shrink-0">
            <Cookie className="w-6 h-6 text-sky-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
              🍪 Wij gebruiken cookies
            </h3>
            <p className="text-slate-600 text-sm md:text-base">
              Deze website gebruikt cookies om uw ervaring te verbeteren en onze diensten te optimaliseren. 
              Door verder te gaan, gaat u akkoord met ons cookiebeleid.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto">
          <Button
            onClick={handleDecline}
            variant="outline"
            className="flex-1 md:flex-none rounded-full px-6 py-2 border-slate-300 text-slate-600 hover:bg-slate-50"
            data-testid="cookie-decline-btn"
          >
            Weigeren
          </Button>
          <Button
            onClick={handleAccept}
            className="flex-1 md:flex-none rounded-full px-6 py-2 bg-sky-500 hover:bg-sky-600 text-white"
            data-testid="cookie-accept-btn"
          >
            Accepteren
          </Button>
        </div>
      </div>
    </div>
  );
};

// Google Reviews Section
const GoogleReviewsSection = () => {
  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="google-reviews-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              {/* Google Logo */}
              <div className="flex-shrink-0">
                <svg className="w-12 h-12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>4.8</span>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-6 h-6 ${star <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400 fill-yellow-400'}`} 
                      />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600">Google Reviews</p>
              </div>
            </div>
            <a
              href="https://www.google.com/search?gs_ssp=eJzj4tVP1zc0zEmxKIxPsqgyYLRSNagwMU82NDNMSTYzTzQzMTBIsjKoSLVMMjJNTbIwSTFLNTK3SPTiSitKLc5QKMstMLQcAa-gTTg&q=fresh+view&rlz=1CDGOYI_enBE1051BE1051&oq=f&gs_lcrp=EgZjaHJvbWUqFQgBEC4YQxivARjHARi6AhiABBiKBTIGCAAQRRg5MhUIARAuGEMYrwEYxwEYugIYgAQYigUyBggCECMYJzIQCAMQLhivARjHARi6AhiABDIHCAQQABiABDITCAUQLhiDARjHARixAxjRAxiABDISCAYQLhhDGMcBGNEDGIAEGIoFMgoIBxAAGLEDGIAEMgwICBAuGEMYgAQYigXSAQgxNTg3ajBqN6gCGrACAeIDBBgBIF_xBZHP_QFHKTbD8QWRz_0BRyk2ww&hl=nl&sourceid=chrome-mobile&ie=UTF-8#mpd=~10832033236040117453/customers/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid="google-reviews-link"
            >
              Bekijk Reviews
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services data - Updated with Oprit Reiniging instead of Tapijt
const services = [
  {
    id: "ramen-wassen",
    title: "Ramen wassen",
    desc: "Streeploos resultaat voor een helder uitzicht.",
    icon: Sparkles,
    image: "/images/ramen.webp",
    cardImage: "/images/ramen-card.webp"
  },
  {
    id: "dakreiniging",
    title: "Dakreiniging",
    desc: "Verwijderen van mos en vuil voor een langer behoud.",
    icon: Home,
    image: "/images/dak.webp",
    cardImage: "/images/dak-card.webp"
  },
  {
    id: "zonnepaneel-schoonmaak",
    title: "Zonnepaneel schoonmaak",
    desc: "Optimaliseer uw rendement met schone panelen.",
    icon: Sun,
    image: "/images/zonnepanelen.webp",
    cardImage: "/images/zonnepanelen-card.webp"
  },
  {
    id: "gevel-reiniging",
    title: "Oprit en gevelreiniging",
    desc: "Softwash reiniging voor een schone oprit, terras en gevel.",
    icon: Car,
    image: "/images/oprit.webp"
  }
];

// Process steps
const processSteps = [
  { step: 1, title: "Aanvraag", desc: "Vul het formulier in." },
  { step: 2, title: "Offerte", desc: "Prijs op maat binnen 24u." },
  { step: 3, title: "Afspraak", desc: "We plannen een datum." },
  { step: 4, title: "Reiniging", desc: "Geniet van het resultaat." }
];

// Features/Benefits
const features = [
  {
    icon: Shield,
    title: "Professioneel",
    desc: "Ervaren team met oog voor detail."
  },
  {
    icon: Leaf,
    title: "Milieuvriendelijk",
    desc: "Duurzame en biologisch afbreekbare producten."
  },
  {
    icon: CheckCircle2,
    title: "100% tevredenheid",
    desc: "Niet tevreden? We komen kosteloos terug."
  },
  {
    icon: Clock,
    title: "Flexibel",
    desc: "Ook beschikbaar in avonden en weekenden."
  }
];

// Header Component
const Header = ({ scrolled, onMenuClick }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header 
      className={`header-sticky py-4 ${scrolled ? 'scrolled' : 'bg-transparent'}`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2" 
            data-testid="logo"
          >
            <img 
              src="/images/logo.webp" 
              alt="Fresh View Logo" 
              width="200"
              height="60"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button 
              onClick={() => scrollToSection('diensten')} 
              className={`${scrolled ? 'text-slate-600' : 'text-white/95 drop-shadow'} hover:text-sky-500 font-medium transition-colors duration-300`}
            >
              Diensten
            </button>
            <button 
              onClick={() => scrollToSection('waarom')} 
              className={`${scrolled ? 'text-slate-600' : 'text-white/95 drop-shadow'} hover:text-sky-500 font-medium transition-colors duration-300`}
            >
              Waarom Wij
            </button>
            <button 
              onClick={() => scrollToSection('proces')} 
              className={`${scrolled ? 'text-slate-600' : 'text-white/95 drop-shadow'} hover:text-sky-500 font-medium transition-colors duration-300`}
            >
              Werkwijze
            </button>
            <button 
              onClick={() => scrollToSection('offerte')} 
              className={`${scrolled ? 'text-slate-600' : 'text-white/95 drop-shadow'} hover:text-sky-500 font-medium transition-colors duration-300`}
            >
              Contact
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:0494614295" 
              className={`flex items-center gap-2 ${scrolled ? 'text-slate-700' : 'text-white/95 drop-shadow'} hover:text-sky-500 font-medium transition-colors duration-300`}
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>0494 61 42 95</span>
            </a>
            <Button 
              onClick={() => scrollToSection('offerte')}
              className="rounded-full px-6 bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              data-testid="header-cta-btn"
            >
              Gratis Offerte
            </Button>
          </div>

          {/* Mobile Menu Button - hidden, replaced by bottom nav */}
        </div>
      </div>
    </header>
  );
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose }) => {
  const scrollToSection = (id) => {
    onClose();
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
  };

  return (
    <>
      <div 
        className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        data-testid="mobile-menu-overlay"
      />
      <div className={`mobile-menu ${isOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <img 
              src="/images/logo.webp" 
              alt="Fresh View Logo" 
              className="h-10 w-auto"
            />
            <button 
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors duration-300"
              data-testid="mobile-menu-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex flex-col gap-4">
            <button 
              onClick={() => scrollToSection('diensten')}
              className="text-left py-3 px-4 text-lg text-slate-700 hover:text-sky-600 hover:bg-sky-50 rounded-xl transition-all duration-300"
            >
              Diensten
            </button>
            <button 
              onClick={() => scrollToSection('waarom')}
              className="text-left py-3 px-4 text-lg text-slate-700 hover:text-sky-600 hover:bg-sky-50 rounded-xl transition-all duration-300"
            >
              Waarom Wij
            </button>
            <button 
              onClick={() => scrollToSection('proces')}
              className="text-left py-3 px-4 text-lg text-slate-700 hover:text-sky-600 hover:bg-sky-50 rounded-xl transition-all duration-300"
            >
              Werkwijze
            </button>
            <button 
              onClick={() => scrollToSection('offerte')}
              className="text-left py-3 px-4 text-lg text-slate-700 hover:text-sky-600 hover:bg-sky-50 rounded-xl transition-all duration-300"
            >
              Contact
            </button>
          </nav>
          <div className="mt-8 pt-8 border-t border-slate-100">
            <a 
              href="tel:0494614295"
              className="flex items-center gap-3 py-3 px-4 text-slate-700 hover:text-sky-600 rounded-xl transition-colors duration-300"
            >
              <Phone className="w-5 h-5" />
              <span className="font-medium">0494 61 42 95</span>
            </a>
            <Button 
              onClick={() => scrollToSection('offerte')}
              className="w-full mt-4 rounded-full py-6 bg-sky-500 hover:bg-sky-600 text-white font-semibold"
            >
              Gratis offerte aanvragen
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-[90vh] sm:min-h-[85vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background image with responsive sources */}
      <picture>
        <source
          media="(min-width: 768px)"
          type="image/webp"
          srcSet="/images/hero-home-landscape-1200.webp 1200w, /images/hero-home-landscape-1600.webp 1600w, /images/hero-home-landscape-1920.webp 1920w"
          sizes="100vw"
        />
        <img
          src="/images/hero-home-portrait-800.webp"
          srcSet="/images/hero-home-portrait-480.webp 480w, /images/hero-home-portrait-800.webp 800w, /images/hero-home-portrait-1200.webp 1200w"
          sizes="100vw"
          alt="Fresh View professionele ramenwasser aan het werk met telescopische stok"
          className="absolute inset-0 w-full h-full object-cover object-[30%_60%] sm:object-[center_center]"
          fetchpriority="high"
          decoding="async"
          data-testid="hero-bg-image"
        />
      </picture>
      {/* Readability overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/65 to-slate-900/30 sm:from-slate-900/80 sm:via-slate-900/50 sm:to-transparent" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent sm:hidden" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full z-10">
        <div className="max-w-3xl">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/25 text-white text-sm font-medium mb-6">
              <Star className="w-4 h-4 fill-sky-400 text-sky-400" />
              Reiniging in heel Vlaams-Brabant
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-sm"
              style={{ fontFamily: 'Instrument Sans, sans-serif' }}
            >
              Ramen, daken, gevels{" "}
              <span className="bg-gradient-to-r from-sky-300 to-emerald-300 bg-clip-text text-transparent">& zonnepanelen</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-100/90 mb-8 leading-relaxed max-w-xl">
              Grondig gereinigd, zonder schade. Altijd een streeploos resultaat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('offerte')}
                className="rounded-full px-8 py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid="hero-cta-btn"
              >
                Gratis offerte
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a 
                href="tel:0494614295"
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-6 text-lg font-medium bg-white/10 backdrop-blur-md ring-1 ring-white/30 text-white hover:bg-white hover:text-slate-900 transition-all duration-300"
                data-testid="hero-phone-btn"
              >
                <Phone className="w-5 h-5" />
                Bel nu
              </a>
            </div>
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/15">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-slate-100/90">Gratis offerte</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-slate-100/90">Geen contracten</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-slate-100/90">Lokaal bedrijf</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section - Now with clickable cards
const ServicesSection = () => {
  return (
    <section id="diensten" className="py-12 sm:py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-8 sm:mb-16">
          <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Onze diensten</span>
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
            style={{ fontFamily: 'Instrument Sans, sans-serif' }}
          >
            Wat we doen.
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg">
            Van ramen tot daken, alles grondig en professioneel gereinigd.
          </p>
        </div>

        {/* Services Grid - Competitor style */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {services.map((service, index) => (
            <Link 
              key={service.id}
              to={`/diensten/${service.id}`}
              className={`group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300 block`}
              data-testid={`service-card-${service.id}`}
            >
              {/* Full image card */}
              <div className="aspect-[4/5] relative overflow-hidden">
                <img 
                  src={(service.cardImage || service.image).replace('.webp','-800.webp')}
                  srcSet={`${(service.cardImage || service.image).replace('.webp','-480.webp')} 480w, ${(service.cardImage || service.image).replace('.webp','-800.webp')} 800w, ${(service.cardImage || service.image).replace('.webp','-1200.webp')} 1200w`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  alt={service.title}
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                {/* Text on image */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-white/80 text-sm line-clamp-2">{service.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section id="waarom" className="py-12 sm:py-20 bg-gradient-to-b from-white to-slate-50" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-8 sm:mb-16">
          <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Waarom Fresh View</span>
          <h2 
            className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
            style={{ fontFamily: 'Instrument Sans, sans-serif' }}
          >
            Kwaliteit die u verdient.
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg">
            Geen gedoe, wel resultaat. Dit is waarom klanten voor ons kiezen.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className={`p-4 sm:p-8 rounded-2xl bg-white border border-slate-100 hover:border-sky-100 hover:shadow-xl transition-all duration-300 animate-fade-in-up stagger-${index + 1}`}
              data-testid={`feature-card-${index}`}
            >
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-sky-600 mb-3 sm:mb-6">
                <feature.icon className="w-5 h-5 sm:w-7 sm:h-7" />
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-slate-900 mb-2 sm:mb-3 leading-tight" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  return (
    <section id="proces" className="py-12 sm:py-20 bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-8 sm:mb-16">
          <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Werkwijze</span>
          <h2 
            className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
            style={{ fontFamily: 'Instrument Sans, sans-serif' }}
          >
            Hoe het werkt.
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg">
            In vier eenvoudige stappen naar een schoon resultaat.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {processSteps.map((step, index) => (
            <div 
              key={step.step}
              className={`relative text-center animate-fade-in-up stagger-${index + 1}`}
              data-testid={`process-step-${step.step}`}
            >
              {/* Connector line for desktop */}
              {index < processSteps.length - 1 && (
                <div className="process-connector hidden lg:block" />
              )}
              <div className="process-step-circle mx-auto mb-6">
                {step.step}
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                {step.title}
              </h3>
              <p className="text-slate-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Form Section
const QuoteFormSection = () => {
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    adres: "",
    dienst: "",
    bericht: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, dienst: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.naam || !formData.email || !formData.telefoon || !formData.adres || !formData.dienst) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to Formspree for email notification
      await axios.post("https://formspree.io/f/xdalonng", {
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        adres: formData.adres,
        dienst: formData.dienst,
        bericht: formData.bericht || "Geen bericht"
      });
      
      // Push to dataLayer for GTM tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'form_submission_success',
        form_name: 'homepage_quote_form',
        form_dienst: formData.dienst
      });
      
      toast.success("Bedankt! Uw aanvraag is verzonden. We nemen snel contact met u op.");
      setFormData({
        naam: "",
        email: "",
        telefoon: "",
        adres: "",
        dienst: "",
        bericht: ""
      });
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Er ging iets mis. Probeer het opnieuw of bel ons.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="offerte" className="py-12 sm:py-20 bg-white" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Gratis offerte</span>
            <h2 
              className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 sm:mt-3 mb-4 sm:mb-6"
              style={{ fontFamily: 'Instrument Sans, sans-serif' }}
            >
              Klaar voor een fris resultaat?
            </h2>
            <p className="text-slate-600 text-base sm:text-lg mb-6 sm:mb-8">
              Laat uw gegevens achter en ontvang binnen 24 uur een prijs op maat.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-4">
              <a 
                href="tel:0494614295"
                className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 hover:bg-sky-100 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-sky-500 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bel ons</p>
                  <p className="font-semibold text-slate-900">0494 61 42 95</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Werkgebied</p>
                  <p className="font-semibold text-slate-900">Leuven en omstreken</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="form-container p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="naam" className="text-slate-700 font-medium mb-2 block">
                    Naam *
                  </Label>
                  <Input
                    id="naam"
                    name="naam"
                    value={formData.naam}
                    onChange={handleChange}
                    placeholder="Uw naam"
                    className="form-input"
                    data-testid="input-naam"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                    E-mail *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="uw@email.be"
                    className="form-input"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="telefoon" className="text-slate-700 font-medium mb-2 block">
                    Telefoon *
                  </Label>
                  <Input
                    id="telefoon"
                    name="telefoon"
                    value={formData.telefoon}
                    onChange={handleChange}
                    placeholder="Uw telefoonnummer"
                    className="form-input"
                    data-testid="input-telefoon"
                  />
                </div>
                <div>
                  <Label htmlFor="dienst" className="text-slate-700 font-medium mb-2 block">
                    Dienst *
                  </Label>
                  <Select value={formData.dienst} onValueChange={handleServiceChange}>
                    <SelectTrigger className="form-input" data-testid="select-dienst">
                      <SelectValue placeholder="Selecteer een dienst" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="adres" className="text-slate-700 font-medium mb-2 block">
                  Adres *
                </Label>
                <Input
                  id="adres"
                  name="adres"
                  value={formData.adres}
                  onChange={handleChange}
                  placeholder="Straat, nummer, gemeente"
                  className="form-input"
                  data-testid="input-adres"
                />
              </div>

              <div>
                <Label htmlFor="bericht" className="text-slate-700 font-medium mb-2 block">
                  Bericht (optioneel)
                </Label>
                <Textarea
                  id="bericht"
                  name="bericht"
                  value={formData.bericht}
                  onChange={handleChange}
                  placeholder="Vertel ons meer over uw project..."
                  className="form-textarea"
                  rows={4}
                  data-testid="input-bericht"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70"
                data-testid="submit-quote-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="spinner" />
                    Verzenden...
                  </span>
                ) : (
                  <>
                    Verstuur Aanvraag
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer id="contact" className="bg-slate-900 text-white pt-20 pb-8 relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <img 
                src="/images/logo.webp" 
                alt="Fresh View Logo" 
                className="h-14 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-slate-400 mb-6">
              Uw betrouwbare partner voor professionele schoonmaakdiensten in Leuven en omstreken.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>Diensten</h4>
            <ul className="space-y-3">
              {services.map(service => (
                <li key={service.id}>
                  <Link 
                    to={`/diensten/${service.id}`}
                    className="text-slate-400 hover:text-sky-400 transition-colors duration-300"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>Snelle Links</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#diensten" className="text-slate-400 hover:text-sky-400 transition-colors duration-300">
                  Onze Diensten
                </a>
              </li>
              <li>
                <a href="/#waarom" className="text-slate-400 hover:text-sky-400 transition-colors duration-300">
                  Waarom Wij
                </a>
              </li>
              <li>
                <a href="/#proces" className="text-slate-400 hover:text-sky-400 transition-colors duration-300">
                  Werkwijze
                </a>
              </li>
              <li>
                <a href="/#offerte" className="text-slate-400 hover:text-sky-400 transition-colors duration-300">
                  Offerte aanvragen
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:0494614295"
                  className="flex items-center gap-3 text-slate-400 hover:text-sky-400 transition-colors duration-300"
                >
                  <Phone className="w-5 h-5" />
                  0494 61 42 95
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-slate-400">
                  <MapPin className="w-5 h-5" />
                  Leuven en omstreken
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Fresh View. Alle rechten voorbehouden.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-500 hover:text-sky-400 text-sm transition-colors duration-300">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-500 hover:text-sky-400 text-sm transition-colors duration-300">
                Algemene Voorwaarden
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen" data-testid="app-container">
      <div className="noise-overlay" />
      
      <Header scrolled={scrolled} onMenuClick={() => setMobileMenuOpen(true)} />
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      
      <main>
        <HeroSection />
        <ServicesSection />
        <FeaturesSection />
        <ProcessSection />
        <TestimonialsSection />
        <QuoteFormSection />
      </main>
      
      <PreFooterBanner />
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

// GTM Page View Tracker for SPA
const GTMPageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Push page view to dataLayer on route change
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: {
        path: location.pathname,
        title: document.title
      }
    });
  }, [location]);
  
  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
};

// SEO-friendly URL alias → redirect to canonical service page
const ServiceRedirect = ({ to }) => <Navigate to={`/diensten/${to}`} replace />;

// Main App Component with Routes
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <GTMPageViewTracker />
      <Toaster position="top-center" richColors />
      <CookieBanner />
      <Suspense fallback={<div className="min-h-screen bg-white" />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/diensten/:serviceId" element={<ServicePage />} />
        {/* SEO-friendly URL aliases - all render same ServicePage via redirect */}
        <Route path="/ramenwassen-leuven" element={<ServiceRedirect to="ramen-wassen" />} />
        <Route path="/glazenwasser-leuven" element={<ServiceRedirect to="ramen-wassen" />} />
        <Route path="/ramen-wassen-leuven" element={<ServiceRedirect to="ramen-wassen" />} />
        <Route path="/dakreiniging-leuven" element={<ServiceRedirect to="dakreiniging" />} />
        <Route path="/dak-reinigen-leuven" element={<ServiceRedirect to="dakreiniging" />} />
        <Route path="/dakgoot-reiniging-leuven" element={<ServiceRedirect to="dakreiniging" />} />
        <Route path="/zonnepanelen-reinigen-leuven" element={<ServiceRedirect to="zonnepaneel-schoonmaak" />} />
        <Route path="/zonnepanelen-leuven" element={<ServiceRedirect to="zonnepaneel-schoonmaak" />} />
        <Route path="/zonnepaneel-schoonmaak-leuven" element={<ServiceRedirect to="zonnepaneel-schoonmaak" />} />
        <Route path="/gevelreiniging-leuven" element={<ServiceRedirect to="gevel-reiniging" />} />
        <Route path="/opritreiniging-leuven" element={<ServiceRedirect to="gevel-reiniging" />} />
        <Route path="/oprit-reinigen-leuven" element={<ServiceRedirect to="gevel-reiniging" />} />
        {/* Old service slug redirect for backwards compatibility */}
        <Route path="/diensten/oprit-reiniging" element={<ServiceRedirect to="gevel-reiniging" />} />
        <Route path="/diensten/zonnepanelen-reinigen" element={<ServiceRedirect to="zonnepaneel-schoonmaak" />} />
        <Route path="/diensten/zonnepanelen" element={<ServiceRedirect to="zonnepaneel-schoonmaak" />} />
        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
